import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ChevronRight, Calendar, Eye, User, Clock, ArrowLeft, Share2, Facebook, Linkedin } from 'lucide-react';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BlogPostPage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedPosts, setRelatedPosts] = useState([]);

  useEffect(() => {
    fetchPost();
  }, [slug]);

  const fetchPost = async () => {
    try {
      const res = await fetch(`${API_URL}/api/blog/${slug}`);
      if (!res.ok) throw new Error('Post not found');
      const data = await res.json();
      setPost(data);
      
      // Fetch related posts
      const allPostsRes = await fetch(`${API_URL}/api/blog?published_only=true&limit=4`);
      const allPosts = await allPostsRes.json();
      setRelatedPosts(allPosts.filter(p => p.id !== data.id).slice(0, 3));
    } catch (error) {
      console.error('Error fetching post:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('sq-AL', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  const getReadingTime = (content) => {
    const wordsPerMinute = 200;
    const words = content.split(/\s+/).length;
    return Math.ceil(words / wordsPerMinute);
  };

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex justify-center items-center">
        <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-slate-50 pt-32">
        <div className="container-custom text-center py-20">
          <h1 className="text-3xl font-bold text-slate-900 mb-4">Postimi nuk u gjet</h1>
          <p className="text-slate-500 mb-8">Artikulli që kërkoni nuk ekziston ose është fshirë.</p>
          <Button asChild className="bg-blue-600 hover:bg-blue-700 rounded-full">
            <Link to="/blog">
              <ArrowLeft className="w-5 h-5 mr-2" />
              Kthehu te Blog
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="blog-post-page" className="min-h-screen bg-slate-50">
      {/* Header */}
      <section className="bg-gradient-to-br from-blue-600 to-blue-800 text-white pt-32 pb-16">
        <div className="container-custom">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-blue-200 text-sm mb-6 flex-wrap">
            <Link to="/" className="hover:text-white transition-colors">Kryefaqja</Link>
            <ChevronRight className="w-4 h-4" />
            <Link to="/blog" className="hover:text-white transition-colors">Blog</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-white line-clamp-1">{post.title}</span>
          </nav>
          
          <span className="inline-flex items-center gap-1 bg-white/20 backdrop-blur px-4 py-1.5 rounded-full text-sm font-medium mb-4">
            {post.category}
          </span>
          
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 max-w-4xl">
            {post.title}
          </h1>
          
          <div className="flex flex-wrap items-center gap-6 text-blue-100">
            <span className="flex items-center gap-2">
              <User className="w-5 h-5" />
              {post.author}
            </span>
            <span className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              {formatDate(post.created_at)}
            </span>
            <span className="flex items-center gap-2">
              <Clock className="w-5 h-5" />
              {getReadingTime(post.content)} min lexim
            </span>
            <span className="flex items-center gap-2">
              <Eye className="w-5 h-5" />
              {post.views} shikime
            </span>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            {/* Cover Image */}
            {post.cover_image && (
              <div className="rounded-2xl overflow-hidden shadow-xl mb-12 -mt-20">
                <img
                  src={post.cover_image}
                  alt={post.title}
                  className="w-full h-auto"
                />
              </div>
            )}
            
            {/* Article Content */}
            <article className="bg-white rounded-2xl shadow-sm border border-slate-200/50 p-8 md:p-12">
              {/* Excerpt */}
              <p className="text-xl text-slate-600 leading-relaxed mb-8 pb-8 border-b border-slate-200 italic">
                {post.excerpt}
              </p>
              
              {/* Main Content */}
              <div 
                className="prose prose-lg prose-slate max-w-none prose-headings:font-bold prose-headings:text-slate-900 prose-p:text-slate-600 prose-a:text-blue-600 prose-img:rounded-xl"
                dangerouslySetInnerHTML={{ __html: post.content.replace(/\n/g, '<br/>') }}
              />
              
              {/* Tags */}
              {post.tags && post.tags.length > 0 && (
                <div className="mt-12 pt-8 border-t border-slate-200">
                  <h4 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4">Etiketa</h4>
                  <div className="flex flex-wrap gap-2">
                    {post.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="px-4 py-2 bg-slate-100 text-slate-600 rounded-full text-sm font-medium"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Share */}
              <div className="mt-8 pt-8 border-t border-slate-200">
                <h4 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4">Ndaj këtë artikull</h4>
                <div className="flex gap-3">
                  <a
                    href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center hover:bg-blue-700 transition-colors"
                  >
                    <Facebook className="w-5 h-5" />
                  </a>
                  <a
                    href={`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(post.title)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-full bg-blue-700 text-white flex items-center justify-center hover:bg-blue-800 transition-colors"
                  >
                    <Linkedin className="w-5 h-5" />
                  </a>
                  <button
                    onClick={() => navigator.clipboard.writeText(shareUrl)}
                    className="w-10 h-10 rounded-full bg-slate-200 text-slate-600 flex items-center justify-center hover:bg-slate-300 transition-colors"
                    title="Kopjo lidhjen"
                  >
                    <Share2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </article>
            
            {/* Back to Blog */}
            <div className="mt-8 text-center">
              <Button asChild variant="outline" className="rounded-full">
                <Link to="/blog">
                  <ArrowLeft className="w-5 h-5 mr-2" />
                  Kthehu te Blog
                </Link>
              </Button>
            </div>
          </div>
          
          {/* Related Posts */}
          {relatedPosts.length > 0 && (
            <div className="mt-20">
              <h2 className="text-2xl font-bold text-slate-900 mb-8 text-center">Artikuj të Ngjashëm</h2>
              <div className="grid md:grid-cols-3 gap-8">
                {relatedPosts.map(relatedPost => (
                  <Link
                    key={relatedPost.id}
                    to={`/blog/${relatedPost.slug}`}
                    className="group bg-white rounded-2xl overflow-hidden shadow-sm border border-slate-200/50 hover:shadow-xl transition-all"
                  >
                    {relatedPost.cover_image && (
                      <div className="h-40 overflow-hidden">
                        <img
                          src={relatedPost.cover_image}
                          alt={relatedPost.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                    )}
                    <div className="p-5">
                      <span className="text-xs text-blue-600 font-medium">{relatedPost.category}</span>
                      <h3 className="font-semibold text-slate-900 mt-1 group-hover:text-blue-600 transition-colors line-clamp-2">
                        {relatedPost.title}
                      </h3>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default BlogPostPage;
